import { computed, Injectable, Signal } from '@angular/core';
import { first, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { ApiClientLabelsService } from 'src/app/api/api-client-labels.service';
import { AssessmentsLabelsStore } from '../state/assessments-labels.store';
import { AssessmentCustomLabel, AssessmentResourceId } from 'src/app/api/models/assessments/assessment.interface';

@Injectable({
     providedIn: 'root'
})
export class AssessmentsLabelsService {

    constructor(
        private apiClientLabelsService: ApiClientLabelsService,
        private store: AssessmentsLabelsStore,
    ) { }

    init(): Observable<void> {
        this.store.loadLabels();
        return of();
    }

    selectLoading(): Observable<boolean | undefined> {
        return this.store.select('loading');
    }

    getLoading(): Signal<boolean | undefined> {
        return this.store.signal('loading');
    }

    getEmpty(): Signal<boolean | undefined> {
        return this.store.signal('empty');
    }

    getLabels(asArray: boolean = false): Signal<Map<AssessmentResourceId, AssessmentCustomLabel> | AssessmentCustomLabel[]> {
        if (asArray) {
            return computed(() => Array.from(new Set([...this.store.signal('labels')().values()])));
        }
        return this.store.signal('labels');
    }

    selectLabels(asArray: boolean = false): Observable<Map<AssessmentResourceId, AssessmentCustomLabel> | AssessmentCustomLabel[]> {
        if (asArray) {
            return this.store.select('labels').pipe(
                map(labels => Array.from(new Set([...labels.values()])))
            );
        }
        return this.store.select('labels');
    }

    createLabel(label: AssessmentCustomLabel): Observable<AssessmentCustomLabel> {
        return this.apiClientLabelsService.createLabel(label).pipe(
           tap(savedLabel => this.store.addLabel(savedLabel))
        );
    }

    /**
     * Resolve a list of labels to fully-persisted labels (with ids): labels already carrying an id pass
     * through; labels without one are matched to an existing label by name (case-insensitive), and only
     * the genuinely-new ones are created. Shared by the New-template dialog and the AI template-writer
     * panel so the find-or-create logic lives in one place; each caller maps the result to its own tagId
     * shape (numeric vs string) for its create endpoint.
     */
    resolveLabels(labels: AssessmentCustomLabel[]): Observable<AssessmentCustomLabel[]> {
        const list = labels ?? [];
        const withoutId = list.filter(label => !label.id);
        if (!withoutId.length) {
            return of(list);
        }
        return this.selectLabels(true).pipe(
            first(),
            switchMap((existing: AssessmentCustomLabel[]) => {
                const findByName = (name: string) => existing.find(l => l.name.trim().toLowerCase() === name.trim().toLowerCase());
                const toCreate = withoutId.filter(label => !findByName(label.name));
                if (!toCreate.length) {
                    return of(list.map(label => label.id ? label : (findByName(label.name) ?? label)));
                }
                return forkJoin(toCreate.map(label => this.createLabel({ name: label.name, id: '' }))).pipe(
                    first(),
                    map(created => list.map(label =>
                        label.id ? label : (findByName(label.name) ?? created.find(c => c.name === label.name) ?? label))),
                );
            }),
        );
    }

    updateLabel(id: AssessmentResourceId, label: AssessmentCustomLabel): Observable<AssessmentCustomLabel> {
        return this.apiClientLabelsService.updateLabel(id, label).pipe(
            tap(() => this.store.updateLabel({ ...label, id }))
        );
    }

    deleteLabel(id: number): Observable<void> {
        return this.apiClientLabelsService.deleteLabel(id).pipe(
            tap(() => this.store.deleteLabel(id))
        );
    }

    getLabelByName(name: string): AssessmentCustomLabel {
        const labels = this.getLabels(true)() as AssessmentCustomLabel[];
        // Case-insensitive per product spec ("label values are case-insensitive for filtering") — the
        // use-case tab strings (Privacy/Vendor/AI) must resolve regardless of the BE-seeded casing.
        const target = name?.toLowerCase();

        return labels.find(label => label.name?.toLowerCase() === target);
    }

    getLabelNames(): Set<string> {
        const labels = this.getLabels(true)() as AssessmentCustomLabel[];
        return new Set(labels.map(label => label.name));
    }

    /**
     * Names of the built-in use-case labels (Privacy / Vendor / AI). Used to hide them from the
     * assessments filter sidebar under Unified Assessments — they are reached via the use-case tabs.
     */
    getDefaultLabelNames(): Set<string> {
        const labels = this.getLabels(true)() as AssessmentCustomLabel[];
        return new Set(labels.filter(label => label.isDefault).map(label => label.name));
    }

    areLabelIdsEqual(
        a: AssessmentCustomLabel[],
        b: AssessmentCustomLabel[]
      ): boolean {
        if (a.length !== b.length) return false;
      
        const idsA = a.map(label => label.id ?? null).sort();
        const idsB = b.map(label => label.id ?? null).sort();
      
        return idsA.every((id, index) => id === idsB[index]);
      }
}